import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPrivacyPolicy, getTermsConditions } from "../../api/apiServices";
import ReactMarkdown from "react-markdown"; // optional — see note below
import PageLoader from "../../components/PageLoader";

// ── Skeleton ──────────────────────────────────────────────────
const Sk = ({ className = "" }) => (
  <div className={`animate-pulse bg-surface-hover rounded-xl ${className}`} />
);

function ContentSkeleton() {
  return (
    <div className="space-y-3 px-5 mt-4">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="bg-surface rounded-2xl border border-border p-5 space-y-3">
          <Sk className="h-4 w-48" />
          <Sk className="h-3 w-full" />
          <Sk className="h-3 w-5/6" />
          <Sk className="h-3 w-4/6" />
        </div>
      ))}
    </div>
  );
}

// ── Render content ────────────────────────────────────────────
// If content is Markdown (stored from admin), render it properly.
// If ReactMarkdown isn't available at all (import fails at module
// load), MD_AVAILABLE is false and we use the plain-paragraph path.
//
// IMPORTANT: we do NOT rely on the `prose` plugin classes here.
// ReactMarkdown renders bare HTML tags (<h1>, <p>, <strong>, <a>...)
// with no color of their own — all styling has to come from
// Tailwind Typography's prose-* utilities. If that plugin isn't
// installed/configured, those tags fall back to the browser default
// (black), which ignores dark mode entirely and is almost certainly
// why text looked dark-on-dark. A try/catch around the JSX return
// also doesn't help — constructing a <ReactMarkdown> element doesn't
// throw; only the later React render pass can fail, by which point
// the catch block has already been skipped.
//
// Fix: pass explicit `components` overrides to ReactMarkdown so every
// element gets our text-color tokens directly, with no dependency on
// the typography plugin being present or configured correctly.
const MD_COMPONENTS = {
  h1: ({ node, ...props }) => <h1 className="text-primary font-bold text-lg mt-5 mb-2" {...props} />,
  h2: ({ node, ...props }) => <h2 className="text-primary font-bold text-base mt-5 mb-2" {...props} />,
  h3: ({ node, ...props }) => <h3 className="text-primary font-bold text-sm mt-4 mb-1.5" {...props} />,
  p:  ({ node, ...props }) => <p className="text-secondary text-sm leading-relaxed my-2" {...props} />,
  li: ({ node, ...props }) => <li className="text-secondary text-sm leading-relaxed my-0.5" {...props} />,
  ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2" {...props} />,
  ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2" {...props} />,
  strong: ({ node, ...props }) => <strong className="text-primary font-semibold" {...props} />,
  em: ({ node, ...props }) => <em className="text-secondary" {...props} />,
  a:  ({ node, ...props }) => <a className="text-brand underline" target="_blank" rel="noopener noreferrer" {...props} />,
  blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-brand-border pl-3 text-muted italic my-2" {...props} />,
  hr: ({ node, ...props }) => <hr className="border-border my-4" {...props} />,
  code: ({ node, ...props }) => <code className="text-brand-text bg-brand-light px-1.5 py-0.5 rounded text-xs" {...props} />,
};

function LegalContent({ content }) {
  if (!content) {
    return (
      <div className="px-5 mt-4">
        <div className="bg-surface rounded-2xl border border-border p-10 text-center">
          <i className="ri-file-text-line text-4xl text-faint mb-3 block" />
          <p className="text-muted text-sm">Content not available yet</p>
        </div>
      </div>
    );
  }

  if (ReactMarkdown) {
    return (
      <div className="px-5 mt-4">
        <div className="bg-surface rounded-2xl border border-border p-5 max-w-none">
          <ReactMarkdown components={MD_COMPONENTS}>{content}</ReactMarkdown>
        </div>
      </div>
    );
  }

  // Fallback if react-markdown isn't installed at all: split on
  // double newlines and render as paragraphs, using the same tokens.
  return (
    <div className="px-5 mt-4">
      <div className="bg-surface rounded-2xl border border-border p-5 space-y-4">
        {content.split(/\n\n+/).filter(Boolean).map((block, i) => {
          // Heading detection (## or #)
          if (/^#{1,3}\s/.test(block)) {
            return (
              <p key={i} className="text-primary font-bold text-sm pt-2">
                {block.replace(/^#+\s/, "")}
              </p>
            );
          }
          // Bold detection (**text**) — strong inherits color, so it
          // needs an explicit class via a wrapping span since we can't
          // pass className through dangerouslySetInnerHTML's raw tags.
          const withBold = block.replace(
            /\*\*(.*?)\*\*/g,
            '<strong style="color: var(--color-text-primary); font-weight: 600;">$1</strong>'
          );
          return (
            <p key={i} className="text-secondary text-sm leading-relaxed"
              dangerouslySetInnerHTML={{ __html: withBold }} />
          );
        })}
      </div>
    </div>
  );
}

export default function TermsPrivacy() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeTab,  setActiveTab]  = useState(searchParams.get("tab") === "privacy" ? "privacy" : "terms");
  const [privacy,    setPrivacy]    = useState(null); // { content, lastUpdated }
  const [terms,      setTerms]      = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [fetched,    setFetched]    = useState({ terms: false, privacy: false });

  // Sync tab from URL
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "privacy" || tab === "terms") setActiveTab(tab);
  }, [searchParams]);

  // Lazy-fetch: only load a document when the user first opens that tab
  useEffect(() => {
    if (fetched[activeTab]) return; // already loaded
    setLoading(true);

    const fn = activeTab === "privacy" ? getPrivacyPolicy : getTermsConditions;
    fn()
      .then(res => {
        if (!res.status) return;
        if (activeTab === "privacy") setPrivacy(res.data);
        else                         setTerms(res.data);
        setFetched(p => ({ ...p, [activeTab]: true }));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeTab]);

  const currentDoc    = activeTab === "privacy" ? privacy : terms;
  const lastUpdated   = currentDoc?.lastUpdated
    ? new Date(currentDoc.lastUpdated).toLocaleDateString("en-NG", { dateStyle: "long" })
    : null;

    if (loading) return <PageLoader size="md" message="Privacy page loading..." />;

  return (
    <div className="min-h-screen bg-bg pb-10 font-display">
      {/* Header */}
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border sticky top-0 z-10">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center flex-shrink-0 hover:bg-surface-hover transition-colors">
            <i className="ri-arrow-left-line text-secondary text-lg" />
          </button>
          <div>
            <h1 className="text-primary font-bold text-lg">Legal Documents</h1>
            {lastUpdated && <p className="text-muted text-xs">Last updated: {lastUpdated}</p>}
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-surface-hover rounded-2xl p-1 gap-1">
          {[
            { key: "terms",   label: "Terms of Service", icon: "ri-file-text-line" },
            { key: "privacy", label: "Privacy Policy",   icon: "ri-shield-user-line" },
          ].map(({ key, label, icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-semibold transition-all ${
                activeTab === key ? "bg-surface text-primary shadow-card" : "text-muted"
              }`}>
              <i className={icon} />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary banner */}
      <div className="px-5 mt-4">
        <div className="bg-brand-light border border-brand-border rounded-2xl px-4 py-4 flex items-start gap-3">
          <div className="w-10 h-10 bg-brand-light rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ boxShadow: "inset 0 0 0 1px var(--color-brand-ring)" }}>
            <i className={`text-brand text-lg ${activeTab === "terms" ? "ri-scales-3-line" : "ri-lock-line"}`} />
          </div>
          <div>
            <p className="text-brand-text text-sm font-bold">
              {activeTab === "terms" ? "Terms of Service" : "Privacy Policy"}
            </p>
            <p className="text-brand-text text-xs mt-0.5 leading-relaxed opacity-90">
              {activeTab === "terms"
                ? "These terms govern your use of Easwallet. By using our services you agree to these terms."
                : "We are committed to protecting your data in compliance with NDPR and Nigerian law."}
            </p>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <ContentSkeleton />
      ) : (
        <LegalContent content={currentDoc?.content} />
      )}

      {/* Footer */}
      <div className="px-5 mt-6 text-center space-y-1.5">
        <p className="text-faint text-xs">
          &copy; {new Date().getFullYear()} Easwallet. All rights reserved.
        </p>
      </div>
    </div>
  );
}